import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {httpClientV2, HttpClientError} from "../../infrastructure/http";

type ApiEnvelope<T> = {
  status: boolean;
  message: string;
  data: T;
};

export type IntegrationsStatus = {
  calendarConnected: boolean;
  zoomConnected: boolean;
  tiktokConnected?: boolean;
  telegramConnected?: boolean;
  metaPixelConnected?: boolean;
};

export const integrationsStatusQueryKey = ["integrations", "status"] as const;
export const telegramStatusQueryKey = ["integrations", "telegram", "status"] as const;
export const telegramGroupsQueryKey = ["integrations", "telegram", "groups"] as const;

export type TelegramStatus = {
  connected: boolean;
  telegramUsername: string | null;
  groupCount: number;
};

export type TelegramGroup = {
  chatId: string;
  chatTitle: string;
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as {message?: unknown}).message === "string"
  ) {
    return (error as {message: string}).message;
  }
  return fallback;
}

/**
 * `GET integrations/telegram/status` → connected Telegram account details.
 */
export function useGetTelegramStatus(options?: {enabled?: boolean}) {
  return useQuery<TelegramStatus, HttpClientError>({
    queryKey: telegramStatusQueryKey,
    queryFn: async () => {
      const res = await httpClientV2.get<ApiEnvelope<TelegramStatus>>(
        "integrations/telegram/status"
      );
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: options?.enabled,
  });
}

type UseGetIntegrationsStatusOptions = {
  enabled?: boolean;
  refetchInterval?:
    | number
    | false
    | ((data: IntegrationsStatus | undefined) => number | false);
};

/**
 * `GET integrations/status` → connection state for all integrations.
 */
export function useGetIntegrationsStatus(options?: UseGetIntegrationsStatusOptions) {
  return useQuery<IntegrationsStatus, HttpClientError>({
    queryKey: integrationsStatusQueryKey,
    queryFn: async () => {
      const res = await httpClientV2.get<ApiEnvelope<IntegrationsStatus>>(
        "integrations/status"
      );
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * `GET integrations/telegram/groups` → Telegram groups available to the coach.
 */
export function useGetTelegramGroups(options?: {enabled?: boolean}) {
  return useQuery<TelegramGroup[], HttpClientError>({
    queryKey: telegramGroupsQueryKey,
    queryFn: async () => {
      const res = await httpClientV2.get<ApiEnvelope<TelegramGroup[]>>(
        "integrations/telegram/groups"
      );
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: options?.enabled,
  });
}

/**
 * `GET integrations/zoom/login` → returns the Zoom OAuth authorization URL that
 * the browser should be redirected to. The backend embeds the coach id in the
 * OAuth `state` and configures the redirect back to the frontend callback route.
 */
export function useConnectZoom() {
  return useMutation<string, HttpClientError, void>({
    mutationFn: async () => {
      const res = await httpClientV2.get<ApiEnvelope<{redirectUrl: string}>>(
        "integrations/zoom/login"
      );
      return res.data.redirectUrl;
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Could not start Zoom connection."));
    },
  });
}

export type ConnectTiktokPayload = {
  pixelId: string;
  accessToken?: string;
};

/**
 * `POST integrations/tiktok` — saves the coach's TikTok Pixel ID (and optional
 * Events API access token), then refreshes the integrations status.
 */
export function useConnectTiktok() {
  const queryClient = useQueryClient();

  return useMutation<boolean, HttpClientError, ConnectTiktokPayload>({
    mutationFn: async (payload) => {
      const res = await httpClientV2.post<ApiEnvelope<boolean>>(
        "integrations/tiktok",
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      message.success("TikTok Pixel connected successfully");
      queryClient.invalidateQueries({queryKey: integrationsStatusQueryKey});
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Could not connect your TikTok Pixel."));
    },
  });
}

/**
 * `DELETE integrations/tiktok` — disconnects the coach's TikTok Pixel, then
 * refreshes the integrations status.
 */
export function useDisconnectTiktok() {
  const queryClient = useQueryClient();

  return useMutation<boolean, HttpClientError, void>({
    mutationFn: async () => {
      const res = await httpClientV2.delete<ApiEnvelope<boolean>>("integrations/tiktok");
      return res.data;
    },
    onSuccess: () => {
      message.success("TikTok Pixel disconnected successfully");
      queryClient.invalidateQueries({queryKey: integrationsStatusQueryKey});
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Could not disconnect your TikTok Pixel."));
    },
  });
}

export type RequestTelegramCodePayload = {
  phoneNumber: string;
};

export type RequestTelegramCodeResult = {
  phoneCodeHash: string;
};

/**
 * `POST integrations/telegram/request-code` — sends a Telegram verification code
 * to the coach's phone number.
 */
export function useRequestTelegramCode() {
  return useMutation<
    RequestTelegramCodeResult,
    HttpClientError,
    RequestTelegramCodePayload
  >({
    mutationFn: async (payload) => {
      const res = await httpClientV2.post<ApiEnvelope<RequestTelegramCodeResult>>(
        "integrations/telegram/request-code",
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      message.success("Verification code sent");
    },
    onError: (error) => {
      message.error(
        getErrorMessage(error, "Could not send the Telegram verification code.")
      );
    },
  });
}

export type VerifyTelegramCodePayload = {
  phoneNumber: string;
  phoneCodeHash: string;
  phoneCode: string;
};

export type VerifyTelegramCodeResult = {
  setupMessageDelivered: boolean;
  botDeepLink: string;
  telegramUsername: string;
};

/**
 * `POST integrations/telegram/verify-code` — completes Telegram connection with
 * the OTP, then refreshes the integrations status.
 */
export function useVerifyTelegramCode() {
  const queryClient = useQueryClient();

  return useMutation<
    VerifyTelegramCodeResult,
    HttpClientError,
    VerifyTelegramCodePayload
  >({
    mutationFn: async (payload) => {
      const res = await httpClientV2.post<ApiEnvelope<VerifyTelegramCodeResult>>(
        "integrations/telegram/verify-code",
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      message.success("Telegram connected successfully");
      queryClient.invalidateQueries({queryKey: integrationsStatusQueryKey});
      queryClient.invalidateQueries({queryKey: telegramStatusQueryKey});
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Could not verify your Telegram code."));
    },
  });
}

/**
 * `DELETE integrations/telegram/disconnect` — disconnects Telegram, then
 * refreshes the integrations status.
 */
export function useDisconnectTelegram() {
  const queryClient = useQueryClient();

  return useMutation<boolean, HttpClientError, void>({
    mutationFn: async () => {
      const res = await httpClientV2.delete<ApiEnvelope<boolean>>(
        "integrations/telegram/disconnect"
      );
      return res.data;
    },
    onSuccess: () => {
      message.success("Telegram disconnected successfully");
      queryClient.invalidateQueries({queryKey: integrationsStatusQueryKey});
      queryClient.removeQueries({queryKey: telegramStatusQueryKey});
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Could not disconnect Telegram."));
    },
  });
}

type CompleteZoomConnectionParams = {
  code: string | null;
  coachId: string | null;
  email: string | null;
};

/**
 * `GET integrations/zoom/callback?code=&coachId=&email=` — completes the OAuth
 * exchange after Zoom redirects back to the frontend callback route. Runs once
 * a `code` is present, then refreshes the integrations status.
 */
export function useCompleteZoomConnection({
  code,
  coachId,
  email,
}: CompleteZoomConnectionParams) {
  const queryClient = useQueryClient();

  return useQuery<boolean, HttpClientError>({
    queryKey: ["integrations", "zoom", "callback", code],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("code", code ?? "");
      if (coachId) params.set("coachId", coachId);
      if (email) params.set("email", email);

      const res = await httpClientV2.get<ApiEnvelope<boolean>>(
        `integrations/zoom/callback?${params.toString()}`
      );
      return res.data;
    },
    enabled: Boolean(code),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: () => {
      message.success("Zoom connected successfully");
      queryClient.invalidateQueries({queryKey: integrationsStatusQueryKey});
    },
    onError: (error) => {
      message.error(getErrorMessage(error, "Could not complete Zoom connection."));
    },
  });
}
